/** JSON anchor on `ContractReviewFlag` — vendor copy lives in `proposedWording`. */
export interface ContractReviewFlagAnchor {
  proposedWording?: string | null;
  [key: string]: unknown;
}

/** PATCH body for `PATCH …/flags/:flagId` (pending flags only). */
export interface UpdateContractReviewFlagRequest {
  title?: string;
  /** Internal rationale visible to reviewers only (maps to `body` column). */
  body?: string;
  /** Canonical vendor-facing message (persisted as `anchor.proposedWording`). */
  vendorMessage?: string | null;
  /**
   * @deprecated Use `vendorMessage`. Accepted for backward compatibility only.
   */
  proposedWording?: string | null;
}

export function readFlagAnchorProposedWording(anchor: unknown): string | null {
  if (!anchor || typeof anchor !== 'object' || Array.isArray(anchor)) return null;
  const value = (anchor as ContractReviewFlagAnchor).proposedWording;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Vendor-facing comment shown in PDF annotations, send-back preview, and external portal.
 *
 * Prefers `anchor.proposedWording` when set. Otherwise falls back to `body` for legacy
 * flags created before internal/vendor split — those may expose reviewer text until edited.
 */
export function resolveContractReviewVendorMessage(body: string, anchor: unknown): string {
  return readFlagAnchorProposedWording(anchor) ?? body.trim();
}

export function mergeFlagAnchorProposedWording(
  anchor: unknown,
  proposedWording: string | null | undefined,
): Record<string, unknown> {
  const base =
    anchor && typeof anchor === 'object' && !Array.isArray(anchor)
      ? { ...(anchor as Record<string, unknown>) }
      : {};
  if (proposedWording === undefined) return base;
  const trimmed = typeof proposedWording === 'string' ? proposedWording.trim() : '';
  base.proposedWording = trimmed.length > 0 ? trimmed : null;
  return base;
}

/** Resolves vendor message field from PATCH DTO (`vendorMessage` wins over deprecated alias). */
export function resolveUpdateContractReviewFlagVendorMessage(
  dto: Pick<UpdateContractReviewFlagRequest, 'vendorMessage' | 'proposedWording'>,
): string | null | undefined {
  if (dto.vendorMessage !== undefined) return dto.vendorMessage;
  return dto.proposedWording;
}

/** True when vendor surfaces would fall back to internal `body` (pre-split legacy flags). */
export function isContractReviewFlagUsingLegacyVendorFallback(
  body: string,
  anchor: unknown,
): boolean {
  return readFlagAnchorProposedWording(anchor) === null && body.trim().length > 0;
}
